import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { volumeService } from '../../services/volumeService';
import { sellingPriceService } from '../../services/sellingPriceService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import { cell, GRID } from '../../styles/gridStyles';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { exportSellingPriceTemplate, parseSellingPriceTemplate } from '../../utils/sellingPricesExcel';

// =============================================================================
// Selling Prices (per FG) — list price per finished good per financial period.
//
// PRICE HIERARCHY (two levels, most specific wins):
//   1. UNIVERSAL  (customer_group_code IS NULL) — the COUNTRY RETAIL PRICE. The
//      default list price applied to every customer.
//   2. CUSTOMER GROUP (customer_group_code set) — if a selling price is set at a
//      customer-group level, THAT PRICE OVERRIDES THE COUNTRY RETAIL PRICE for
//      every customer in that group.
//
// Nothing is copied down: the override is resolved at consumption time (see the
// LATERAL in discountController.getDiscounts, which picks the group price when
// one exists and falls back to the country retail price otherwise). So leaving a
// group cell blank / 0 simply means "no override — use the country retail price".
//
// The "Price scope" selector switches the whole grid between Universal (country
// retail) and each customer group — every finished-good SKU is a row, the twelve
// month cells edit inline.
//
// Built on the shared MantleGrid with the same Excel Import/Export round-trip
// as the volume screens (overwrite / replace). Version comes from the top bar.
// =============================================================================

const IMPORT_MODES = [
  { id: 'overwrite', label: 'Overwrite', hint: 'Replace the price of any matching SKU / group / month.' },
  { id: 'replace', label: 'Replace all', hint: "Wipe this version's prices (all scopes) first, then load the file." },
];

const keyOf = (stock, group) => `${stock}|${group || ''}`;

// ---- Import / Export modal --------------------------------------------------
const PortModal = ({ mode, setMode, isPorting, importErrors, onExport, onImport, onClose }) => (
  <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
    onMouseDown={(e) => { if (e.target === e.currentTarget && !isPorting) onClose(); }}>
    <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Import / Export — Selling Prices</h3>
        <button type="button" onClick={onClose} disabled={isPorting}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5">
        <h4 className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-2 uppercase tracking-wide">On import, existing prices should be…</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {IMPORT_MODES.map((m) => (
            <label key={m.id}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                mode === m.id ? 'border-slate-900 bg-slate-50 dark:bg-slate-700' : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
              }`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="portMode" className="accent-slate-900" checked={mode === m.id} onChange={() => setMode(m.id)} />
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{m.label}</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">{m.hint}</p>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Excel pre-filled with the current scope. The SKU and Customer Group columns carry dropdowns; enter prices under P1–P12.
            </p>
            <button onClick={onExport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 disabled:opacity-50">
              {isPorting ? 'Working…' : 'Export'}
            </button>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Upload a completed template. The Customer Group column decides each row's scope (blank = Universal). If anything is off, nothing is saved.
            </p>
            <button onClick={onImport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
              {isPorting ? 'Working…' : `Import (${IMPORT_MODES.find((m) => m.id === mode).label})`}
            </button>
          </div>
        </div>

        {importErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-700 mb-1.5">{importErrors.length} row(s) need attention — nothing was imported:</p>
            <ul className="text-xs text-red-600 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
              {importErrors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---- Page -------------------------------------------------------------------
const SellingPrices = () => {
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [skus, setSkus] = useState([]);              // finished-good reference list
  const [groups, setGroups] = useState([]);          // customer-group reference list
  const [scope, setScope] = useState('');            // '' = universal, else a customer_group_code
  const [pricesByKey, setPricesByKey] = useState({}); // { `${stock}|${group}`: { prices{p}, uom } } — all scopes
  const [rows, setRows] = useState([]);              // working rows for the current scope
  const [dirty, setDirty] = useState({});            // { `${stock}:${p}`: true }
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [portOpen, setPortOpen] = useState(false);
  const [portMode, setPortMode] = useState('overwrite');
  const [isPorting, setIsPorting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  // ---- Load reference (finished-good SKUs + customer groups) once ----------
  useEffect(() => {
    (async () => {
      try {
        const r = await volumeService.getReferenceData();
        if (r.success) { setSkus(r.data.stockCodes || []); setGroups(r.data.customerGroups || []); }
      } catch { /* reference empty -> grid shows nothing */ }
    })();
  }, []);

  // ---- Load prices (all scopes) --------------------------------------------
  const loadPrices = useCallback(async () => {
    if (!selectedVersion) { setPricesByKey({}); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await sellingPriceService.getSellingPrices(selectedVersion);
      const map = {};
      (res.success ? res.data : []).forEach((r) => {
        const prices = {};
        Object.entries(r.period_data || {}).forEach(([p, c]) => { prices[Number(p)] = Number(c.price) || 0; });
        map[keyOf(r.stock_code, r.customer_group_code)] = { prices, uom: r.unit_of_measure || null };
      });
      setPricesByKey(map);
    } catch (e) {
      setError('Failed to load selling prices.');
      setPricesByKey({});
    } finally {
      setIsLoading(false);
    }
  }, [selectedVersion]);

  useEffect(() => { loadPrices(); }, [loadPrices]);

  // ---- Rebuild working rows for the current scope --------------------------
  // Every finished-good SKU is a row; overlay this scope's saved prices. Runs
  // when the scope, the loaded prices, or the SKU universe change. Switching
  // scope resets the working edits (save before switching).
  useEffect(() => {
    setRows(skus.map((s) => {
      const entry = pricesByKey[keyOf(s.stock_code, scope)];
      return {
        stock_code: s.stock_code,
        description: s.description,
        uom: entry?.uom || s.unit_of_measure || null,
        prices: entry?.prices ? { ...entry.prices } : {},
      };
    }));
    setDirty({});
  }, [skus, pricesByKey, scope]);

  // ---- Grid model ----------------------------------------------------------
  const priceOf = (r, p) => Number(r.prices?.[p] || 0);
  const rowAvg = (r) => PERIODS.map((p) => priceOf(r, p)).reduce((a, v) => a + v, 0) / (PERIODS.length || 1);

  const columns = useMemo(() => ([
    { columnId: 'sku', width: 300, resizable: true },
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 80, resizable: true })),
    { columnId: 'avg', width: 110, resizable: true },
  ]), []);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header('SKU'),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
        cell.header('Avg Price', { align: 'right' }),
      ],
    };

    const dataRows = rows.map((r, idx) => ({
      rowId: r.stock_code,
      height: GRID.size.dataRow,
      cells: [
        cell.label(`${r.stock_code}${r.description ? ' · ' + r.description : ''}`, { idx }),
        ...PERIODS.map((p) => cell.number(priceOf(r, p), { editable: true, idx })),
        cell.aggregate(Math.round(rowAvg(r) * 100) / 100, { kind: 'year' }),
      ],
    }));

    const avgRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('AVG (ALL SKUS)'),
        ...PERIODS.map((p) => cell.totalNumber(
          Math.round((rows.reduce((a, r) => a + priceOf(r, p), 0) / (rows.length || 1)) * 100) / 100
        )),
        cell.totalBlank(),
      ],
    };

    return [headerRow, ...dataRows, avgRow];
  }, [rows, fyStartMonth]);

  // ---- Edits ---------------------------------------------------------------
  const handleChanges = (changes) => {
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r, prices: { ...r.prices } }));
      const touched = {};
      for (const ch of changes) {
        const col = String(ch.columnId);
        if (!col.startsWith('P')) continue;
        const period = Number(col.slice(1));
        if (!(period >= 1 && period <= 12)) continue;
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        const row = next.find((r) => r.stock_code === ch.rowId);
        if (!row) continue;
        row.prices[period] = val;
        touched[`${row.stock_code}:${period}`] = true;
      }
      setDirty((prevDirty) => ({ ...prevDirty, ...touched }));
      return next;
    });
  };

  // ---- Save ----------------------------------------------------------------
  const handleSave = async () => {
    setError(null);
    const entries = [];
    for (const r of rows) {
      for (const p of PERIODS) {
        if (!dirty[`${r.stock_code}:${p}`]) continue;
        entries.push({ stock_code: r.stock_code, customer_group_code: scope || null, financial_period: p, price: priceOf(r, p), unit_of_measure: r.uom || null });
      }
    }
    if (entries.length === 0) return;

    setIsSaving(true);
    try {
      const res = await sellingPriceService.upsertSellingPrices({ version_id: selectedVersion, mode: 'overwrite', rows: entries });
      if (!res.success) throw new Error(res.message);
      await loadPrices();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save selling prices.');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Import / Export -----------------------------------------------------
  const exportRows = useMemo(
    () => rows.map((r) => ({ stock_code: r.stock_code, description: r.description, unit_of_measure: r.uom, prices: r.prices })),
    [rows]
  );

  const handleExport = async () => {
    setImportErrors([]); setIsPorting(true);
    try {
      await exportSellingPriceTemplate({ versionLabel: selectedVersion, skus, groups, scopeGroup: scope, rows: exportRows });
    } catch { setError('Failed to generate the Excel template.'); }
    finally { setIsPorting(false); }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportErrors([]); setError(null); setIsPorting(true);
    try {
      const { rows: parsed, errors } = await parseSellingPriceTemplate(file, skus, groups);
      if (errors.length) { setImportErrors(errors); return; }
      if (parsed.length === 0 && portMode !== 'replace') { setError('No data rows found in the template.'); return; }
      const res = await sellingPriceService.upsertSellingPrices({ version_id: selectedVersion, mode: portMode, rows: parsed });
      if (!res.success) throw new Error(res.message);
      setPortOpen(false);
      await loadPrices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to read or import the template.');
    } finally { setIsPorting(false); }
  };

  const hasChanges = Object.keys(dirty).length > 0;
  const selectClass =
    'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-slate-400';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Header is one line to keep the grid tall: title, the override rule as a
          badge, and the scope selector. The longer guidance lives in the select's
          tooltip and the grid hint so it costs no vertical space. */}
      <div className="shrink-0 px-6 pt-5 pb-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Selling Prices (per FG)</h1>
          <span className="px-2 py-1 rounded text-xs font-semibold whitespace-nowrap border border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200">
            Customer Group pricing overrides Universal pricing
          </span>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap">Price scope</span>
            <select
              className={selectClass}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              title={scope
                ? 'Group override — a blank / 0 cell means no override, so that SKU uses the Universal (country retail) price. Switching scope discards unsaved edits.'
                : 'Universal = the country retail price, the default for every customer. Pick a customer group to enter prices that override it.'}
            >
              <option value="">Universal (country retail)</option>
              {groups.map((g) => (
                <option key={g.customer_group_code} value={g.customer_group_code}>
                  {g.customer_group_code}{g.group_name ? ` — ${g.group_name}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && <div className="mt-2 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!selectedVersion ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Select a budget version in the top bar to begin.
            </div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              hint={scope
                ? 'Group override — blank / 0 means no override, so that SKU uses the country retail price. Switching scope discards unsaved edits.'
                : 'Country retail price — the default for every customer. Prices per case, per month; price × volume drives the gross revenue budget.'}
              toolbar={
                <>
                  <MantleGridButton onClick={() => { setImportErrors([]); setPortOpen(true); }}>Import / Export</MantleGridButton>
                  <MantleGridButton onClick={handleSave} disabled={isSaving || !hasChanges} accent={hasChanges}>
                    {isSaving ? 'Saving…' : 'Save'}
                  </MantleGridButton>
                </>
              }
            />
          )}
        </div>
      </div>

      {portOpen && (
        <>
          <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileSelected} />
          <PortModal
            mode={portMode}
            setMode={setPortMode}
            isPorting={isPorting}
            importErrors={importErrors}
            onExport={handleExport}
            onImport={() => fileInputRef.current?.click()}
            onClose={() => { if (!isPorting) setPortOpen(false); }}
          />
        </>
      )}
    </div>
  );
};

export default SellingPrices;
